<template>
  <div class="dashboard-container">
    <!-- Full Screen Map -->
    <MapComponent
      ref="mapComponentRef"
      :data-centers="filteredDataCenters"
      :loading="loading"
      :map-style="selectedMapStyle"
      :color-by="colorByOwnership ? 'capital' : 'status'"
      @data-center-select="handleDataCenterSelect"
      class="fullscreen-map"
    />

    <!-- Tron-Style Status Bar - Positioned over map -->
    <div class="tron-status-bar-overlay">
      <v-sheet class="tron-status-bar py-1 px-3" elevation="2">
        <div class="d-flex align-center justify-space-between">
          <!-- Left side: System status -->
          <div class="d-flex align-center">
            <v-chip size="small" class="mr-3 status-chip">
              <v-icon start>mdi-circle</v-icon>
              System Online
            </v-chip>
            <v-chip size="small" class="status-chip">
              <v-icon start>mdi-server</v-icon>
              {{ dataCenterCount }} Data Centers
            </v-chip>
          </div>

          <!-- Right side: User controls -->
          <div class="d-flex align-center">
            <v-menu>
              <template v-slot:activator="{ props }">
                <v-chip
                  v-bind="props"
                  size="small"
                  class="mr-2 status-chip map-style-chip"
                  title="Map Style"
                >
                  <v-icon start>mdi-map</v-icon>
                  {{ mapStyleOptions.find(option => option.value === selectedMapStyle)?.title || 'Dark' }}
                </v-chip>
              </template>
              <v-list class="tron-menu-list">
                <v-list-item
                  v-for="style in mapStyleOptions"
                  :key="style.value"
                  :value="style.value"
                  @click="selectedMapStyle = style.value"
                  :class="{ 'tron-active-item': selectedMapStyle === style.value }"
                  class="tron-menu-item"
                >
                  <v-list-item-title>{{ style.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <div class="d-flex align-center">
              <v-btn
                @click="showAnalytics = !showAnalytics"
                icon="mdi-chart-bar"
                size="small"
                variant="text"
                :color="showAnalytics ? 'primary' : 'default'"
                title="Toggle Analytics"
                class="control-btn"
              />
              <v-btn
                @click="showTable = !showTable"
                icon="mdi-table"
                size="small"
                variant="text"
                :color="showTable ? 'primary' : 'default'"
                title="Toggle Data Table"
                class="control-btn"
              />
              <v-btn
                @click="showFilters = !showFilters"
                icon="mdi-filter-variant"
                size="small"
                variant="text"
                :color="showFilters ? 'primary' : 'default'"
                title="Toggle Filters"
                class="control-btn"
              />
              <v-btn
                @click="refreshData"
                :loading="loading"
                icon="mdi-refresh"
                size="small"
                variant="text"
                title="Refresh Data"
                class="control-btn"
              />
              <v-btn
                @click="showAcknowledgement = true"
                icon="mdi-information-outline"
                size="small"
                variant="text"
                title="About & Credits"
                class="control-btn"
              />
            </div>
          </div>
        </div>
      </v-sheet>
    </div>

    <!-- Filters Sidebar - Slides in from right -->
    <transition name="slide-right">
      <div v-if="showFilters" class="tron-filters-sidebar">
        <v-card flat class="tron-filters-card">
          <v-card-title class="tron-filters-header pa-3">
            <div class="d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon class="mr-3 tron-icon">mdi-filter-variant</v-icon>
                <span class="tron-title-text">Filters</span>
              </div>
              <v-btn
                @click="showFilters = false"
                icon="mdi-close"
                size="small"
                variant="text"
              />
            </div>
          </v-card-title>

          <v-divider class="tron-divider" />

          <v-card-text class="pa-4">
            <!-- Reset All Filters Button -->
            <v-btn
              @click="resetAllFilters"
              block
              variant="outlined"
              prepend-icon="mdi-filter-remove"
              class="mb-4 reset-filters-btn"
            >
              Reset All Filters
            </v-btn>

            <!-- Filter dropdowns -->
            <div class="mb-4">
              <v-select
                v-model="selectedCountry"
                :items="countryOptions"
                label="Filter by Country"
                density="compact"
                variant="outlined"
                clearable
                class="mb-3 filter-input"
              />
              <v-select
                v-model="selectedCapitalCategory"
                :items="capitalCategoryOptions"
                label="Filter by Ownership"
                density="compact"
                variant="outlined"
                clearable
                class="mb-3 filter-input"
              />
              <v-text-field
                v-model="searchQuery"
                label="Search data centers"
                density="compact"
                variant="outlined"
                prepend-inner-icon="mdi-magnify"
                clearable
                class="filter-input"
              />
            </div>

            <!-- Facility Age Range Slider -->
            <v-divider class="tron-divider mb-4" />

            <div class="mb-4">
              <div class="d-flex align-center justify-space-between mb-2">
                <label class="filter-label">Facility Age (years)</label>
                <span class="range-display">{{ facilityAgeRange[0] }} - {{ facilityAgeRange[1] }}</span>
              </div>
              <v-range-slider
                v-model="facilityAgeRange"
                :min="facilityAgeMin"
                :max="facilityAgeMax"
                :step="1"
                color="black"
                track-color="grey-lighten-2"
                thumb-color="black"
                hide-details
                class="mt-2"
              />
              <div class="d-flex justify-space-between mt-1">
                <span class="range-label">{{ facilityAgeMin }} years</span>
                <span class="range-label">{{ facilityAgeMax }} years</span>
              </div>
            </div>

            <!-- Toggle switches -->
            <v-divider class="tron-divider mb-4" />

            <div class="toggle-section">
              <v-switch
                v-model="showCapacityOnly"
                label="Show only with capacity data"
                density="compact"
                color="primary"
                hide-details
                class="tron-switch mb-3"
              />
              <v-switch
                v-model="colorByOwnership"
                label="Color by Ownership"
                density="compact"
                color="primary"
                hide-details
                class="tron-switch"
              />
            </div>
          </v-card-text>
        </v-card>

        <!-- Backdrop -->
        <div class="tron-filters-backdrop" @click="showFilters = false"></div>
      </div>
    </transition>

    <!-- Analytics Sidebar - Slides in from right -->
    <transition name="slide-right">
      <div v-if="showAnalytics" class="tron-analytics-sidebar">
        <v-card flat class="tron-analytics-card">
          <v-card-title class="tron-analytics-header pa-3">
            <div class="d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon class="mr-3 tron-icon">mdi-chart-bar</v-icon>
                <span class="tron-title-text">Analytics</span>
              </div>
              <v-btn
                @click="showAnalytics = false"
                icon="mdi-close"
                size="small"
                variant="text"
              />
            </div>
          </v-card-title>

          <v-divider class="tron-divider" />

          <v-card-text class="pa-0 analytics-content">
            <RegionalCapacityChart :data-centers="dataCenters" />
          </v-card-text>
        </v-card>

        <!-- Backdrop -->
        <div class="tron-analytics-backdrop" @click="showAnalytics = false"></div>
      </div>
    </transition>

    <!-- Data Table Sidebar - Slides in from left -->
    <transition name="slide-left">
      <div v-if="showTable" class="tron-table-sidebar">
        <v-card flat class="tron-table-card">
          <v-card-title class="tron-table-header pa-3">
            <div class="d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon class="mr-3 tron-icon">mdi-table</v-icon>
                <span class="tron-title-text">Data Centers</span>
                <v-chip size="small" class="ml-3" variant="outlined" style="background: #ffffff; border: 1px solid #000000; color: #000000;">
                  {{ filteredDataCenters.length }}
                </v-chip>
              </div>
              <v-btn
                @click="showTable = false"
                icon="mdi-close"
                size="small"
                variant="text"
              />
            </div>
          </v-card-title>

          <v-divider class="tron-divider" />

          <v-card-text class="pa-0 flex-grow-1" style="overflow: hidden;">
            <v-data-table
              :headers="tableHeaders"
              :items="filteredDataCenters"
              :items-per-page="10"
              class="tron-data-table"
              density="compact"
              hover
              fixed-header
              height="100%"
              @click:row="handleTableRowClick"
            >
              <template v-slot:item.power_capacity="{ item }">
                {{ formatCapacity(item) }}
              </template>

              <template v-slot:item.building_area="{ item }">
                {{ formatBuildingArea(item) }}
              </template>

              <template v-slot:item.facility_age="{ item }">
                {{ formatFacilityAge(item) }}
              </template>

              <template v-slot:item.ownership="{ item }">
                {{ formatCapitalInvestment(item) }}
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <!-- Backdrop -->
        <div class="tron-table-backdrop" @click="showTable = false"></div>
      </div>
    </transition>

    <!-- Minimalist Loading Overlay -->
    <v-overlay v-if="loading" class="align-center justify-center tron-loading-overlay">
      <div class="tron-loading-container">
        <div class="tron-loading-spinner"></div>
        <div class="tron-loading-text">Loading data centers...</div>
      </div>
    </v-overlay>

    <!-- Data Center Details Dialog - Tron Themed -->
    <v-dialog v-model="detailsDialog" max-width="600" class="tron-dialog">
      <v-card v-if="selectedDataCenter" class="tron-dialog-card">
        <v-card-title class="tron-dialog-title d-flex align-center pa-4">
          <v-icon class="mr-3 tron-icon">mdi-server</v-icon>
          <span class="tron-title-text">{{ selectedDataCenter.name }}</span>
        </v-card-title>
        
        <v-card-text class="tron-dialog-content pa-4">
          <v-row>
            <v-col cols="12" md="6">
              <div class="tron-info-item mb-3">
                <span class="tron-label">Location:</span>
                <span class="tron-value">{{ formatLocation(selectedDataCenter) }}</span>
              </div>
              <div class="tron-info-item mb-3">
                <span class="tron-label">Company:</span>
                <span class="tron-value">{{ selectedDataCenter.company_name || 'N/A' }}</span>
              </div>
              <div class="tron-info-item mb-3">
                <span class="tron-label">Power Capacity:</span>
                <span class="tron-value">{{ formatCapacity(selectedDataCenter) }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="tron-info-item mb-3">
                <span class="tron-label">Building Area:</span>
                <span class="tron-value">{{ formatBuildingArea(selectedDataCenter) }}</span>
              </div>
              <div class="tron-info-item mb-3">
                <span class="tron-label">Facility Age:</span>
                <span class="tron-value">{{ formatFacilityAge(selectedDataCenter) }}</span>
              </div>
              <div class="tron-info-item mb-3">
                <span class="tron-label">Ownership:</span>
                <span class="tron-value">{{ formatCapitalInvestment(selectedDataCenter) }}</span>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        
        <!-- Estimation Note -->
        <v-divider class="tron-divider" />
        <v-card-text class="pa-3">
          <div class="text-caption text-center" style="color: #666666; opacity: 1;" v-if="selectedDataCenter && selectedDataCenter.estimated_capacity">
            <v-icon size="12" class="mr-1">mdi-information</v-icon>
            * Estimated capacity based on building area analysis
          </div>
        </v-card-text>
        
        <v-card-actions class="tron-dialog-actions px-4 py-3">
          <v-spacer />
          <v-btn 
            @click="detailsDialog = false" 
            class="tron-close-btn" 
            variant="outlined"
            size="small"
          >
            <v-icon start size="small">mdi-close</v-icon>
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Acknowledgement Overlay -->
    <v-dialog v-model="showAcknowledgement" fullscreen transition="dialog-bottom-transition">
      <v-card class="acknowledgement-card">
        <v-toolbar class="acknowledgement-toolbar">
          <v-toolbar-title class="acknowledgement-title">
            <v-icon size="large" class="mr-2">mdi-information-outline</v-icon>
            About & Acknowledgements
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            @click="showAcknowledgement = false"
            class="close-btn"
          ></v-btn>
        </v-toolbar>

        <v-card-text class="acknowledgement-content pa-8">
          <v-container>
            <v-row>
              <v-col cols="12" md="8" offset-md="2">
                <!-- Logo and Project Title -->
                <div class="text-center mb-8">
                  <img src="/logo.png" alt="DSET" class="acknowledgement-logo mb-6" />
                  <h1 class="project-title mb-2">Data Center Dashboard</h1>
                  <p class="project-subtitle">Singapore & Malaysia Data Center Interactive Map</p>
                  <p class="version-tag">Version 1.10.0</p>
                </div>

                <v-divider class="my-6"></v-divider>

                <!-- Data & Methodology -->
                <section class="mb-8">
                  <h2 class="section-title mb-4">
                    <v-icon class="mr-2">mdi-database-outline</v-icon>
                    Data & Methodology
                  </h2>
                  <h3 class="subsection-title mt-4">Data Sources</h3>
                  <p class="mb-3">
                    This dashboard presents datacenter information for Singapore and Malaysia,
                    including facility specifications, power capacity, ownership, and location data.
                  </p>
                  <p class="mb-3">
                    The data is gathered, aggregated, and compiled from publicly available sources.
                  </p>
                  <p class="mb-3">
                    Ownership markings are conducted by DSET research team based on publicly available news, data, and reports.
                  </p>
                  <h3 class="subsection-title mt-4">Capacity Estimation</h3>
                  <p class="mb-2">
                    For datacenters with missing capacity data, we employ a geographic-calibrated
                    estimation methodology based on analysis of datacenters:
                  </p>
                  <p class="text-caption">
                    * Estimated values are marked with an asterisk and noted in tooltips
                  </p>
                </section>

                <v-divider class="my-6"></v-divider>

                <!-- Credits -->
                <section class="mb-8">
                  <h2 class="section-title mb-4">
                    <v-icon class="mr-2">mdi-account-group-outline</v-icon>
                    Credits & Acknowledgements
                  </h2>
                  <ul class="credit-list">
                    <li>
                      <strong>Data gathering and aggregation:</strong> Economic Security Team, DSET
                    </li>
                    <li>
                      <strong>Visualization & Development:</strong> Built by Yun-Ting Cai, DSET Data Team
                    </li>
                    <li>
                      <strong>Map Data:</strong> © Mapbox, © OpenStreetMap contributors
                    </li>
                  </ul>
                </section>

                <v-divider class="my-6"></v-divider>

                <!-- License & Usage -->
                <section class="mb-4">
                  <h2 class="section-title mb-4">
                    <v-icon class="mr-2">mdi-license</v-icon>
                    Usage & License
                  </h2>
                  <p class="mb-3">
                    This dashboard is provided for informational and research purposes.
                    Datacenter information is compiled from publicly available sources.
                  </p>
                  <p class="text-caption text-medium-emphasis">
                    For questions, corrections, or data updates, please contact the project maintainers.
                  </p>
                </section>

                <!-- Footer -->
                <div class="text-center mt-8 pt-4">
                  <p class="text-caption text-medium-emphasis">
                    Last Updated: January 2026 | Version 1.10.0
                  </p>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick, defineAsyncComponent } from "vue";
import RegionalCapacityChart from "../components/RegionalCapacityChart.vue";

// Lazy load MapComponent with loading and error states
const MapComponent = defineAsyncComponent({
  loader: () => import("../components/MapComponent.vue"),
  loadingComponent: {
    template: `
      <div class="d-flex justify-center align-center" style="height: 100vh; background: #ffffff;">
        <div style="display: flex; flex-direction: column; align-items: center; gap: 16px;">
          <div style="width: 48px; height: 48px; border: 3px solid #e0e0e0; border-top-color: #000000; border-radius: 50%; animation: spin 1s linear infinite;"></div>
          <div style="color: #000000; font-weight: 400; font-size: 0.875rem;">Loading map...</div>
        </div>
      </div>
    `
  },
  errorComponent: {
    template: `
      <div class="d-flex justify-center align-center" style="height: 100vh; background: #ffffff;">
        <div style="max-width: 400px; padding: 24px; border: 1px solid #000000; background: #ffffff;">
          <div style="color: #000000; font-weight: 500; margin-bottom: 8px;">Error</div>
          <div style="color: #666666; font-size: 0.875rem;">Failed to load map component. Please refresh the page.</div>
        </div>
      </div>
    `
  },
  delay: 200,
  timeout: 10000
});

export default {
  name: "DashboardView",
  components: {
    MapComponent,
    RegionalCapacityChart,
  },
  setup() {
    const dataCenters = ref([]);
    const loading = ref(true);
    const showFilters = ref(false);
    const showTable = ref(false);
    const showAnalytics = ref(false);
    const selectedCountry = ref("");
    const searchQuery = ref("");
    const showCapacityOnly = ref(false);
    const selectedCapitalCategory = ref('');
    const facilityAgeRange = ref([0, 100]); // Default range [min, max]
    const mapComponentRef = ref(null);
    const selectedDataCenter = ref(null);
    const detailsDialog = ref(false);
    const selectedMapStyle = ref('light'); // Default to light map style
    const colorByOwnership = ref(false); // Toggle for ownership coloring
    const showAcknowledgement = ref(false); // Acknowledgement overlay toggle

    const countryOptions = computed(() => {
      const countries = [...new Set(dataCenters.value.map((dc) => dc.country))];
      return countries.map((country) => ({ title: country, value: country }));
    });

    const mapStyleOptions = [
      { title: 'Dark', value: 'dark' },
      { title: 'Light', value: 'light' },
      { title: 'Satellite', value: 'satellite' }
    ];

    const tableHeaders = [
      { title: 'Name', key: 'name', sortable: true, width: '180px' },
      { title: 'Location', key: 'location', sortable: true, width: '150px' },
      { title: 'Company', key: 'company_name', sortable: true, width: '140px' },
      { title: 'Power Capacity', key: 'power_capacity', sortable: true, width: '130px' },
      { title: 'Building Area', key: 'building_area', sortable: true, width: '120px' },
      { title: 'Facility Age', key: 'facility_age', sortable: true, width: '100px' },
      { title: 'Ownership', key: 'ownership', sortable: true, width: '160px' }
    ];

    const capitalCategoryOptions = [
      { title: 'Non-Chinese Capital', value: 'non-chinese' },
      { title: 'Chinese Capital', value: 'chinese' },
      { title: 'Joint Venture', value: 'joint' },
      { title: 'No Ownership Data', value: 'none' }
    ];

    const filteredDataCenters = computed(() => {
      let filtered = dataCenters.value;

      if (selectedCountry.value) {
        filtered = filtered.filter(
          (dc) => dc.country === selectedCountry.value,
        );
      }

      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter(
          (dc) =>
            dc.name.toLowerCase().includes(query) ||
            dc.company_name?.toLowerCase().includes(query) ||
            dc.location.toLowerCase().includes(query),
        );
      }

      if (showCapacityOnly.value) {
        filtered = filtered.filter((dc) => {
          // Check if datacenter has any capacity data
          return dc.estimated_capacity || dc.power_totalmw || dc.capacity_mw_builtout;
        });
      }

      // Filter by facility age range - only if user has adjusted the slider
      if (facilityAgeRange.value) {
        const [minAge, maxAge] = facilityAgeRange.value;
        const isDefaultRange = minAge === facilityAgeMin.value && maxAge === facilityAgeMax.value;

        // Only apply filter if user has moved the slider from default position
        if (!isDefaultRange) {
          filtered = filtered.filter((dc) => {
            const age = dc.facility_age;
            // Include datacenters with valid age within range
            if (age == null || isNaN(age) || age < 0) {
              return false; // Exclude datacenters without valid age data when filtering
            }
            return age >= minAge && age <= maxAge;
          });
        }
      }

      if (selectedCapitalCategory.value) {
        filtered = filtered.filter((dc) => {
          const ownership = (dc.ch_capital_en || '').toLowerCase();

          if (selectedCapitalCategory.value === 'none') {
            return !dc.ch_capital_en || dc.ch_capital_en === '';
          }

          switch (selectedCapitalCategory.value) {
            case 'non-chinese': return ownership.includes('non-chinese');
            case 'chinese': return ownership.includes('chinese') && !ownership.includes('non-chinese');
            case 'joint': return ownership.includes('joint');
            default: return true;
          }
        });
      }

      return filtered;
    });

    const dataCenterCount = computed(() => filteredDataCenters.value.length);

    // Compute min and max facility age from all datacenters
    const facilityAgeMin = computed(() => {
      const ages = dataCenters.value
        .map(dc => dc.facility_age)
        .filter(age => age != null && !isNaN(age) && age >= 0);
      return ages.length > 0 ? Math.min(...ages) : 0;
    });

    const facilityAgeMax = computed(() => {
      const ages = dataCenters.value
        .map(dc => dc.facility_age)
        .filter(age => age != null && !isNaN(age) && age >= 0);
      return ages.length > 0 ? Math.max(...ages) : 100;
    });

    // Initialize facility age range when data loads
    watch([facilityAgeMin, facilityAgeMax], () => {
      if (facilityAgeRange.value[0] === 0 && facilityAgeRange.value[1] === 100) {
        facilityAgeRange.value = [facilityAgeMin.value, facilityAgeMax.value];
      }
    });

    const getStatusColor = (status) => {
      switch (status) {
        case "operational":
          return "success";
        case "maintenance":
          return "warning";
        case "offline":
          return "error";
        default:
          return "default";
      }
    };

    const loadDataCenters = async () => {
      try {
        console.log('[DashboardView] loadDataCenters - setting loading to true');
        loading.value = true;
        console.log('[DashboardView] Fetching data centers...');

        // Direct API call (no authentication required - protected by Cloudflare Zero Trust)
        const response = await fetch('/api/datacenters');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        console.log('[DashboardView] Received data centers:', data?.length || 0);
        dataCenters.value = data || [];
      } catch (error) {
        console.error("[DashboardView] Failed to load data centers:", error);
        // Fallback to empty array to prevent map errors
        dataCenters.value = [];
      } finally {
        console.log('[DashboardView] loadDataCenters - setting loading to false');
        loading.value = false;
      }
    };

    const refreshData = () => {
      loadDataCenters();
    };

    const resetAllFilters = () => {
      selectedCountry.value = '';
      selectedCapitalCategory.value = '';
      searchQuery.value = '';
      showCapacityOnly.value = false;
      colorByOwnership.value = false;
      facilityAgeRange.value = [facilityAgeMin.value, facilityAgeMax.value];
    };

    const handleDataCenterSelect = (dataCenter) => {
      selectedDataCenter.value = dataCenter;
      detailsDialog.value = true;
    };

    const handleTableRowClick = (event, { item }) => {
      // Close the table sidebar
      showTable.value = false;

      // Navigate map to datacenter location
      if (item.coordinates && item.coordinates.length === 2) {
        nextTick(() => {
          if (mapComponentRef.value && typeof mapComponentRef.value.flyToLocation === 'function') {
            mapComponentRef.value.flyToLocation(
              item.coordinates[0], // longitude
              item.coordinates[1], // latitude
              16, // zoom level
              item // pass the full datacenter object for tooltip
            );
          }
        });
      }
    };

    // Helper functions for formatting datacenter information
    const formatCapacity = (dc) => {
      if (!dc) return 'N/A';
      
      const capacity = dc.capacity_mw_builtout || dc.power_totalmw;
      if (capacity) {
        const suffix = dc.estimated_capacity ? ' MW*' : ' MW';
        return `${capacity}${suffix}`;
      }
      return 'N/A';
    };

    const formatLocation = (dc) => {
      if (!dc) return 'N/A';
      const city = dc.city || '';
      const country = dc.country || '';
      if (city && country) return `${city}, ${country}`;
      if (city) return city;
      if (country) return country;
      return 'N/A';
    };

    const formatBuildingArea = (dc) => {
      if (!dc) return 'N/A';
      
      // Use building_area_unit_sqm column directly
      if (dc.building_area_unit_sqm !== null && dc.building_area_unit_sqm !== undefined && dc.building_area_unit_sqm !== '') {
        return `${dc.building_area_unit_sqm.toLocaleString()} m²`;
      }
      
      return 'N/A';
    };

    const formatISOCertification = (dc) => {
      if (!dc) return 'N/A';
      if (dc.standards_iso27001 === '1' || dc.standards_iso27001 === '1.0') return 'Certified';
      if (dc.standards_iso27001 === '0' || dc.standards_iso27001 === '0.0') return 'Not Certified';
      return 'N/A';
    };

    const formatPCICompliance = (dc) => {
      if (!dc) return 'N/A';
      if (dc.standards_pci === '1' || dc.standards_pci === '1.0') return 'Compliant';
      if (dc.standards_pci === '0' || dc.standards_pci === '0.0') return 'Not Compliant';
      return 'N/A';
    };

    const formatSOC2Compliance = (dc) => {
      if (!dc) return 'N/A';
      if (dc.standards_soc2 === '1' || dc.standards_soc2 === '1.0') return 'Compliant';
      if (dc.standards_soc2 === '0' || dc.standards_soc2 === '0.0') return 'Not Compliant';
      return 'N/A';
    };

    const formatBooleanValue = (value) => {
      if (value === 'True' || value === '1' || value === 1 || value === true) return 'Yes';
      if (value === 'False' || value === '0' || value === 0 || value === false) return 'No';
      return 'N/A';
    };

    const formatFacilityAge = (dc) => {
      if (dc && dc.facility_age && !isNaN(dc.facility_age)) {
        const age = parseInt(dc.facility_age);
        if (age === 0) return 'New';
        if (age === 1) return '1 year';
        return `${age} years`;
      }

      // Fallback to calculating from building_year_operational
      if (dc && dc.building_year_operational && !isNaN(dc.building_year_operational)) {
        const currentYear = new Date().getFullYear();
        const age = currentYear - parseInt(dc.building_year_operational);
        if (age === 0) return 'New';
        if (age === 1) return '1 year';
        return `${age} years`;
      }

      return 'N/A';
    };

    const formatCapitalInvestment = (dc) => {
      // Display capital ownership type (English translation)
      if (dc && dc.ch_capital_en && dc.ch_capital_en !== '') {
        return dc.ch_capital_en;
      }

      // Fallback to Chinese if English not available
      if (dc && dc.ch_capital && dc.ch_capital !== '') {
        return dc.ch_capital;
      }

      return 'N/A';
    };

    onMounted(() => {
      console.log('[DashboardView] Component mounted, loading data centers...');
      loadDataCenters();

      // Show About overlay every time dashboard loads
      showAcknowledgement.value = true;
    });

    return {
      dataCenters,
      filteredDataCenters,
      loading,
      showFilters,
      showTable,
      showAnalytics,
      selectedCountry,
      searchQuery,
      showCapacityOnly,
      selectedDataCenter,
      detailsDialog,
      countryOptions,
      dataCenterCount,
      getStatusColor,
      refreshData,
      handleDataCenterSelect,
      handleTableRowClick,
      selectedMapStyle,
      mapStyleOptions,
      colorByOwnership,
      showAcknowledgement,
      tableHeaders,
      selectedCapitalCategory,
      capitalCategoryOptions,
      formatCapacity,
      formatLocation,
      formatBuildingArea,
      formatISOCertification,
      formatPCICompliance,
      mapComponentRef,
      formatSOC2Compliance,
      formatBooleanValue,
      formatFacilityAge,
      formatCapitalInvestment,
      facilityAgeRange,
      facilityAgeMin,
      facilityAgeMax,
      resetAllFilters,
    };
  },
};
</script>

<style scoped>
/* Minimalist Black and White Dashboard Styles */

/* Container */
.dashboard-container {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: #ffffff;
}

.fullscreen-map {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

/* Status Bar */
.tron-status-bar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

.tron-status-bar {
  background: #000000 !important;
  border: none !important;
  border-bottom: 1px solid #333333 !important;
  border-radius: 0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3) !important;
  color: #ffffff !important;
  padding: 12px 20px !important;
  transition: all 0.2s ease;
}

/* Control Buttons */
.control-btn {
  height: 36px !important;
  width: 36px !important;
  border-radius: 0 !important;
  transition: all 0.2s ease !important;
  color: #ffffff !important;
}

.control-btn:hover {
  background: #333333 !important;
}

/* Status Chips */
.status-chip {
  background: #ffffff !important;
  color: #000000 !important;
  border: 1px solid #000000 !important;
  border-radius: 0 !important;
  font-weight: 400 !important;
  letter-spacing: 0px !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
}

.status-chip:hover {
  background: #f5f5f5 !important;
}

.status-chip :deep(.v-icon) {
  color: #000000 !important;
}

/* Map style chip - slightly different styling */
.map-style-chip {
  cursor: pointer;
}

.map-style-chip:hover {
  background: #f0f0f0 !important;
  border-color: #000000 !important;
}

/* General chips fallback */
:deep(.v-chip) {
  border-radius: 0 !important;
  font-weight: 500 !important;
  letter-spacing: 0px !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
}

/* Menu List */
.tron-menu-list {
  background: #ffffff !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 0 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  min-width: 140px !important;
  padding: 4px !important;
}

.tron-menu-item {
  color: #000000 !important;
  font-weight: 400 !important;
  font-size: 0.9rem !important;
  border-radius: 0 !important;
  margin: 0 !important;
  padding: 10px 16px !important;
  transition: all 0.2s ease !important;
}

.tron-menu-item:hover {
  background: #f5f5f5 !important;
  color: #000000 !important;
}

.tron-active-item {
  background: #f0f0f0 !important;
  color: #000000 !important;
  font-weight: 500 !important;
}

/* Filters Sidebar */
.tron-filters-sidebar {
  position: fixed;
  top: 49px;
  right: 0;
  bottom: 0;
  width: 320px;
  z-index: 950;
  display: flex;
}

.tron-filters-card {
  width: 320px;
  height: 100%;
  background: #ffffff !important;
  border: none !important;
  border-left: 1px solid #e0e0e0 !important;
  border-radius: 0 !important;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.05) !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.2s ease;
}

.tron-filters-header {
  background: #f5f5f5 !important;
  border-bottom: 1px solid #e0e0e0 !important;
  padding: 16px 20px !important;
}

.tron-filters-header .tron-icon {
  color: #000000 !important;
}

.tron-filters-header .tron-title-text {
  font-size: 1rem;
  font-weight: 600;
  color: #000000;
  letter-spacing: 0px;
}

.tron-filters-backdrop {
  position: fixed;
  top: 49px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: -1;
}

/* Analytics Sidebar */
.tron-analytics-sidebar {
  position: fixed;
  top: 49px;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100vw;
  z-index: 940;
  display: flex;
}

.tron-analytics-card {
  width: 100%;
  height: 100%;
  background: #ffffff !important;
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.2s ease;
}

.tron-analytics-header {
  background: #f5f5f5 !important;
  border-bottom: 1px solid #e0e0e0 !important;
  padding: 16px 20px !important;
}

.tron-analytics-header .tron-icon {
  color: #000000 !important;
}

.tron-analytics-header .tron-title-text {
  font-size: 1rem;
  font-weight: 600;
  color: #000000;
  letter-spacing: 0px;
}

.analytics-content {
  flex: 1;
  overflow-y: auto;
  background: #ffffff;
}

.tron-analytics-backdrop {
  display: none; /* Full page, no backdrop needed */
}

.toggle-section {
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 0;
  padding: 16px;
  transition: all 0.2s ease;
}

/* Form Inputs */
.tron-filters-card :deep(.v-label) {
  color: #666666 !important;
  font-weight: 400 !important;
  font-size: 0.875rem !important;
}

.tron-filters-card :deep(.v-field) {
  border-radius: 0 !important;
  transition: all 0.2s ease !important;
}

.tron-filters-card :deep(.v-field__input) {
  color: #000000 !important;
  font-weight: 400 !important;
  padding: 8px 12px !important;
}

.tron-filters-card :deep(.v-field__outline) {
  color: #000000 !important;
  --v-field-border-width: 1px !important;
}

.tron-filters-card :deep(.v-field:hover .v-field__outline) {
  color: #000000 !important;
}

.tron-filters-card :deep(.v-field--focused .v-field__outline) {
  color: #000000 !important;
  --v-field-border-width: 2px !important;
}

.tron-filters-card :deep(.v-select__selection-text) {
  color: #000000 !important;
  font-weight: 400 !important;
}

.tron-filters-card :deep(.v-icon) {
  color: #666666 !important;
}

/* Dialog */
.tron-dialog :deep(.v-overlay__content) {
  background: transparent !important;
}

.tron-dialog-card {
  background: #ffffff !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 0 !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
  color: #000000 !important;
  overflow: hidden;
  transition: all 0.2s ease;
}

.tron-dialog-title {
  background: #f5f5f5 !important;
  border-bottom: 1px solid #e0e0e0 !important;
  color: #000000 !important;
  font-weight: 600 !important;
  padding: 20px 24px !important;
}

.tron-title-text {
  font-size: 1.25rem !important;
  color: #000000 !important;
  font-weight: 600 !important;
}

.tron-icon {
  color: #000000 !important;
  font-size: 1.5rem !important;
}

.tron-dialog-content {
  color: #000000 !important;
  padding: 24px !important;
}

.tron-info-item {
  display: block;
  line-height: 1.8;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.2s ease;
}

.tron-info-item:last-child {
  border-bottom: none;
}

.tron-info-item:hover {
  background: #f9f9f9;
  padding-left: 8px;
}

.tron-label {
  color: #666666 !important;
  font-weight: 500 !important;
  font-size: 0.875rem !important;
  margin-right: 8px;
}

.tron-value {
  color: #000000 !important;
  font-weight: 400 !important;
  font-size: 0.875rem !important;
}

.tron-status-chip {
  border-radius: 0 !important;
}

.tron-dialog-actions {
  border-top: 1px solid #e0e0e0 !important;
  min-height: auto !important;
  margin-top: 0 !important;
}

.tron-close-btn {
  border: 1px solid #000000 !important;
  color: #000000 !important;
  font-weight: 500 !important;
  font-size: 0.875rem !important;
  height: 36px !important;
  padding: 0 20px !important;
  border-radius: 0 !important;
  transition: all 0.2s ease !important;
}

.tron-close-btn:hover {
  background: #f5f5f5 !important;
  border-color: #000000 !important;
  color: #000000 !important;
}

/* Divider */
.tron-divider {
  border-color: #e0e0e0 !important;
  margin: 0 !important;
}

/* Loading Overlay */
.tron-loading-overlay {
  background: rgba(255, 255, 255, 0.95) !important;
}

.tron-loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.tron-loading-spinner {
  width: 48px;
  height: 48px;
  border: 3px solid #e0e0e0;
  border-top-color: #000000;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.tron-loading-text {
  color: #000000 !important;
  font-weight: 400 !important;
  font-size: 0.875rem !important;
}

.tron-loading-bars {
  display: none; /* Hide loading bars for minimalist design */
}

/* Switch */
.tron-switch :deep(.v-switch__track) {
  background: #e0e0e0 !important;
  border: 1px solid #cccccc !important;
}

.tron-switch :deep(.v-switch--inset .v-switch__track) {
  background: #f5f5f5 !important;
  border: 1px solid #cccccc !important;
}

.tron-switch :deep(.v-switch__thumb) {
  background: #000000 !important;
  box-shadow: none !important;
}

.tron-switch :deep(.v-label) {
  color: #000000 !important;
  font-weight: 400 !important;
  font-size: 0.875rem !important;
}

/* Range Slider Styles */
.filter-label {
  color: #000000;
  font-size: 0.875rem;
  font-weight: 500;
}

.range-display {
  color: #000000;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 2px 8px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 0;
}

.range-label {
  color: #666666;
  font-size: 0.75rem;
}

/* Reset Filters Button */
.reset-filters-btn {
  border: 1px solid #000000 !important;
  color: #000000 !important;
  background: #ffffff !important;
  border-radius: 0 !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
}

.reset-filters-btn:hover {
  background: #f5f5f5 !important;
  border-color: #000000 !important;
}

/* Filter Inputs (Select and TextField) */
.filter-input :deep(.v-field) {
  border-radius: 0 !important;
  border: 1px solid #000000 !important;
  background: #ffffff !important;
}

.filter-input :deep(.v-field--focused) {
  border: 1px solid #000000 !important;
  box-shadow: none !important;
}

.filter-input :deep(.v-field__outline) {
  display: none !important;
}

.filter-input :deep(.v-label) {
  color: #666666 !important;
  font-weight: 400 !important;
}

.filter-input :deep(.v-field__input) {
  color: #000000 !important;
  font-weight: 400 !important;
}

.filter-input :deep(.v-field--variant-outlined .v-field__overlay) {
  display: none !important;
}

.filter-input :deep(.v-icon) {
  color: #000000 !important;
}

/* Data Table Sidebar */
.tron-table-sidebar {
  position: fixed;
  top: 49px;
  left: 0;
  bottom: 0;
  width: 1000px;
  z-index: 900;
  display: flex;
}

.tron-table-card {
  width: 1000px;
  height: 100%;
  background: #ffffff !important;
  border: none !important;
  border-right: 1px solid #e0e0e0 !important;
  border-radius: 0 !important;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05) !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.2s ease;
}

.tron-table-backdrop {
  position: fixed;
  top: 49px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: -1;
}

.tron-table-header {
  background: #f5f5f5 !important;
  border-bottom: 1px solid #e0e0e0 !important;
  padding: 16px 20px !important;
}

.tron-table-header .tron-icon {
  color: #000000 !important;
}

.tron-table-header .tron-title-text {
  font-size: 1rem;
  font-weight: 600;
  color: #000000;
  letter-spacing: 0px;
}

/* Data Table */
.tron-data-table {
  background: transparent !important;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tron-data-table :deep(.v-table__wrapper) {
  background: transparent !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  flex-grow: 1;
  max-height: 100%;
}

.tron-data-table :deep(.v-table__wrapper)::-webkit-scrollbar {
  width: 8px;
}

.tron-data-table :deep(.v-table__wrapper)::-webkit-scrollbar-track {
  background: #f5f5f5;
}

.tron-data-table :deep(.v-table__wrapper)::-webkit-scrollbar-thumb {
  background: #cccccc;
  border-radius: 0;
}

.tron-data-table :deep(.v-table__wrapper)::-webkit-scrollbar-thumb:hover {
  background: #999999;
}

.tron-data-table :deep(th) {
  background: #f5f5f5 !important;
  color: #000000 !important;
  font-weight: 600;
  font-size: 0.875rem;
  border-bottom: 1px solid #e0e0e0 !important;
  padding: 12px 16px !important;
}

.tron-data-table :deep(td) {
  color: #000000 !important;
  border-bottom: 1px solid #f0f0f0 !important;
  font-size: 0.875rem;
  padding: 12px 16px !important;
}

.tron-data-table :deep(tr:hover td) {
  background: #f9f9f9 !important;
  cursor: pointer;
}

.tron-data-table :deep(.v-data-table-footer) {
  background: #f5f5f5 !important;
  color: #000000 !important;
  border-top: 1px solid #e0e0e0 !important;
}

.tron-data-table :deep(.v-pagination__item) {
  background: #ffffff !important;
  color: #000000 !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 0 !important;
}

.tron-data-table :deep(.v-pagination__item--is-active) {
  background: #000000 !important;
  color: #ffffff !important;
}

/* Slide Animations */
.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.3s ease;
}

.slide-left-enter-from {
  transform: translateX(-100%);
}

.slide-left-leave-to {
  transform: translateX(-100%);
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease;
}

.slide-right-enter-from {
  transform: translateX(100%);
}

.slide-right-leave-to {
  transform: translateX(100%);
}

/* Animations */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Map Container */
:deep(.map-container) {
  width: 100%;
  height: 100%;
}

:deep(.map) {
  width: 100%;
  height: 100%;
}

/* Acknowledgement Overlay Styles */
.acknowledgement-card {
  background: #ffffff !important;
  color: #000000 !important;
}

.acknowledgement-toolbar {
  background: #000000 !important;
  color: #ffffff !important;
  border-bottom: 2px solid #000000;
}

.acknowledgement-title {
  font-size: 1.5rem !important;
  font-weight: 600 !important;
  color: #ffffff !important;
}

.acknowledgement-toolbar .close-btn {
  color: #ffffff !important;
}

.acknowledgement-content {
  background: #ffffff;
  overflow-y: auto;
  max-height: calc(100vh - 64px);
}

/* Logo in acknowledgement */
.acknowledgement-logo {
  width: 240px;
  height: auto;
  display: block;
  margin: 0 auto;
}

.project-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #000000;
  letter-spacing: -0.5px;
}

.project-subtitle {
  font-size: 1.25rem;
  color: #666666;
  margin-bottom: 0.5rem;
}

.version-tag {
  display: inline-block;
  padding: 4px 12px;
  background: #f5f5f5;
  border: 1px solid #000000;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: #000000;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #000000;
  display: flex;
  align-items: center;
  border-bottom: 2px solid #000000;
  padding-bottom: 8px;
}

.subsection-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #000000;
  margin-bottom: 12px;
  margin-top: 8px;
}

.tech-list,
.feature-list,
.credit-list {
  list-style: none;
  padding-left: 0;
}

.tech-list li,
.feature-list li,
.credit-list li {
  padding: 6px 0;
  padding-left: 20px;
  position: relative;
  color: #333333;
  line-height: 1.6;
}

.tech-list li:before,
.feature-list li:before,
.credit-list li:before {
  content: "▪";
  position: absolute;
  left: 0;
  color: #000000;
  font-weight: bold;
}

.tech-list strong,
.feature-list strong,
.credit-list strong {
  color: #000000;
  font-weight: 600;
}

.acknowledgement-content p {
  color: #333333;
  line-height: 1.7;
}

.acknowledgement-content :deep(.v-divider) {
  border-color: #e0e0e0 !important;
  opacity: 1 !important;
}

/* Scrollbar styling for acknowledgement content */
.acknowledgement-content::-webkit-scrollbar {
  width: 8px;
}

.acknowledgement-content::-webkit-scrollbar-track {
  background: #f5f5f5;
}

.acknowledgement-content::-webkit-scrollbar-thumb {
  background: #cccccc;
  border-radius: 0;
}

.acknowledgement-content::-webkit-scrollbar-thumb:hover {
  background: #999999;
}
</style>

